import React, { useRef, useState, useEffect } from "react";

let clipboard = [];

export default function SvgModal({ points, setPoints, onClose }) {
  const svgRef = useRef(null);

  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);

  const [tempMode, setTempMode] = useState(null);
  const [selectedColor, setSelectedColor] = useState("#ff0000");

  const [tempLine, setTempLine] = useState(null);
  const [tempRect, setTempRect] = useState(null);

  const [selectionRect, setSelectionRect] = useState(null);
  const [draggingShapes, setDraggingShapes] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const GRID_SIZE = 50;

  // -------------------------
  // COORDINATES
  // -------------------------
  const getSvgCoords = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - offset.x) / zoom,
      y: (e.clientY - rect.top - offset.y) / zoom,
    };
  };

  // -------------------------
  // MOUSE EVENTS
  // -------------------------
  const handleMouseDown = (e) => {
    const { x, y } = getSvgCoords(e);

    // Check if clicked on any selected shape
    const clickedShape = points.find(
      (p) =>
        p.selected &&
        ((p.type === "line" &&
          Math.abs(p.x1 - x) < 10 && Math.abs(p.y1 - y) < 10) ||
          (p.type === "rect" &&
            x >= p.x &&
            x <= p.x + p.width &&
            y >= p.y &&
            y <= p.y + p.height) ||
          (!p.type && Math.hypot(p.x - x, p.y - y) <= p.r))
    );

    if (clickedShape) {
      // Start dragging shapes
      setDraggingShapes(true);
      setDragOffset({ x, y });
      return;
    }

    // If tempMode drawing shapes
    if (tempMode === "line") {
      setTempLine({ x1: x, y1: y, x2: x, y2: y, color: selectedColor });
    } else if (tempMode === "rect") {
      setTempRect({ x, y, width: 0, height: 0, color: selectedColor });
    } else if (tempMode === "ball") {
      const newBall = {
        id: "ball-" + Date.now(),
        x,
        y,
        r: 10,
        color: selectedColor,
      };
      setPoints((prev) => [...prev, newBall]);
    } else {
      // Start drag-select
      setSelectionRect({ x, y, width: 0, height: 0 });
    }
  };

  const handleMouseMove = (e) => {
    const { x, y } = getSvgCoords(e);

    if (draggingShapes) {
      const dx = x - dragOffset.x;
      const dy = y - dragOffset.y;
      setPoints((prev) =>
        prev.map((p) => (p.selected ? { ...p, x: (p.x || p.x1 || 0) + dx, y: (p.y || p.y1 || 0) + dy, x1: p.x1, y1: p.y1 } : p))
      );
      setDragOffset({ x, y });
      return;
    }

    if (selectionRect) {
      setSelectionRect((p) => ({
        ...p,
        width: x - p.x,
        height: y - p.y,
      }));
      return;
    }

    if (tempLine) setTempLine((p) => ({ ...p, x2: x, y2: y }));
    if (tempRect)
      setTempRect((p) => ({ ...p, width: x - p.x, height: y - p.y }));
  };

  const handleMouseUp = () => {
    if (tempLine) {
      const final = { ...tempLine, id: "line-" + Date.now(), type: "line" };
      setPoints((prev) => [...prev, final]);
      setTempLine(null);
    }
    if (tempRect) {
      const x0 = Math.min(tempRect.x, tempRect.x + tempRect.width);
      const y0 = Math.min(tempRect.y, tempRect.y + tempRect.height);
      const w = Math.abs(tempRect.width);
      const h = Math.abs(tempRect.height);
      const final = {
        ...tempRect,
        x: x0,
        y: y0,
        width: w,
        height: h,
        id: "rect-" + Date.now(),
        type: "rect",
      };
      setPoints((prev) => [...prev, final]);
      setTempRect(null);
    }
    if (selectionRect) {
      const { x, y, width, height } = selectionRect;
      const x0 = Math.min(x, x + width);
      const y0 = Math.min(y, y + height);
      const x1 = Math.max(x, x + width);
      const y1 = Math.max(y, y + height);

      setPoints((prev) =>
        prev.map((p) => {
          const px = p.x || p.x1 || 0;
          const py = p.y || p.y1 || 0;
          const pw = p.width || 0;
          const ph = p.height || 0;
          const inside =
            (p.type === "rect" && px + pw >= x0 && px <= x1 && py + ph >= y0 && py <= y1) ||
            (p.type === "line" && px >= x0 && px <= x1 && py >= y0 && py <= y1) ||
            (!p.type && px >= x0 && px <= x1 && py >= y0 && py <= y1);
          return inside ? { ...p, selected: true } : p;
        })
      );
      setSelectionRect(null);
    }

    setDraggingShapes(false);
    setDragStart(null);
  };

  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [tempLine, tempRect, selectionRect, draggingShapes]);

  // -------------------------
  // SELECTION AND PROPERTIES
  // -------------------------
  const selectedShapes = points.filter((p) => p.selected);

  const updateShapes = (updated) => {
    setPoints((prev) =>
      prev.map((p) => updated.find((u) => u.id === p.id) || p)
    );
  };

  const deleteSelected = () => {
    setPoints((prev) => prev.filter((p) => !p.selected));
  };
  const copySelected = () => {
    clipboard = selectedShapes.map((s) => ({ ...s, id: s.id + "-copy" }));
  };
  const pasteClipboard = () => {
    const pasted = clipboard.map((s) => ({
      ...s,
      x: s.x + 20,
      y: s.y + 20,
      selected: false,
      id: s.id + "-" + Date.now(),
    }));
    setPoints((prev) => [...prev, ...pasted]);
  };

  // -------------------------
  // KEYBOARD
  // -------------------------
  useEffect(() => {
    const keyHandler = (e) => {
      if (e.key === "Escape") setPoints((prev) => prev.map((p) => ({ ...p, selected: false })));
      if (e.key === "Delete") deleteSelected();
      if (e.ctrlKey && e.key === "c") copySelected();
      if (e.ctrlKey && e.key === "v") pasteClipboard();
    };
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, [selectedShapes]);

  // -------------------------
  // GRID
  // -------------------------
  const renderGrid = () => {
    const svg = svgRef.current;
    if (!svg) return null;
    const vw = svg.clientWidth;
    const vh = svg.clientHeight;
    const left = -offset.x / zoom;
    const right = (vw - offset.x) / zoom;
    const top = -offset.y / zoom;
    const bottom = (vh - offset.y) / zoom;
    const sX = Math.floor(left / GRID_SIZE) * GRID_SIZE;
    const eX = Math.ceil(right / GRID_SIZE) * GRID_SIZE;
    const sY = Math.floor(top / GRID_SIZE) * GRID_SIZE;
    const eY = Math.ceil(bottom / GRID_SIZE) * GRID_SIZE;
    const out = [];
    for (let x = sX; x <= eX; x += GRID_SIZE)
      out.push(<line key={`vx${x}`} x1={x} y1={top} x2={x} y2={bottom} stroke="#333" strokeWidth={0.5 / zoom} />);
    for (let y = sY; y <= eY; y += GRID_SIZE)
      out.push(<line key={`hy${y}`} x1={left} y1={y} x2={right} y2={y} stroke="#333" strokeWidth={0.5 / zoom} />);
    return out;
  };

  const getCursor = () => {
    if (tempMode) return "crosshair";
    return draggingShapes ? "grabbing" : "grab";
  };

  // -------------------------
  // RENDER
  // -------------------------
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ width: "80%", height: "80%", background: "#000", borderRadius: 6, display: "flex", flexDirection: "column" }}>
        {/* HEADER */}
        <div style={{ background: "#1976d2", padding: "8px 12px", color: "#fff", display: "flex", justifyContent: "space-between" }}>
          <b>SVG Plan Viewer</b>
          <button onClick={onClose} style={{ background: "transparent", color: "#fff", border: "none", fontSize: 18 }}>✖</button>
        </div>

        <div style={{ flex: 1, display: "flex" }}>
          {/* LEFT CANVAS */}
          <div
            style={{ flex: 4, position: "relative", cursor: getCursor(), borderRight: "1px solid #333" }}
            onWheel={(e) => {
              e.preventDefault();
              const factor = e.deltaY > 0 ? 0.9 : 1.1;
              setZoom((z) => Math.min(10, Math.max(0.1, z * factor)));
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
          >
            <svg ref={svgRef} width="100%" height="100%">
              <g transform={`translate(${offset.x},${offset.y}) scale(${zoom})`}>
                {renderGrid()}

                {points.map((p) => {
                  if (p.type === "line") return <line key={p.id} {...p} stroke={p.color} strokeWidth={2/zoom} style={{ opacity: p.selected ? 0.5 : 1 }} />;
                  if (p.type === "rect") return <rect key={p.id} x={p.x} y={p.y} width={p.width} height={p.height} fill={p.color + "33"} stroke={p.color} strokeWidth={2/zoom} style={{ opacity: p.selected ? 0.5 : 1 }} />;
                  return <circle key={p.id} cx={p.x} cy={p.y} r={p.r} fill={p.color} style={{ opacity: p.selected ? 0.5 : 1 }} />;
                })}

                {tempLine && <line {...tempLine} stroke={tempLine.color} strokeWidth={2 / zoom} strokeDasharray="4" />}
                {tempRect && <rect x={Math.min(tempRect.x,tempRect.x+tempRect.width)} y={Math.min(tempRect.y,tempRect.y+tempRect.height)} width={Math.abs(tempRect.width)} height={Math.abs(tempRect.height)} fill={tempRect.color + "33"} stroke={tempRect.color} strokeDasharray="4" strokeWidth={2/zoom} />}
                {selectionRect && <rect x={Math.min(selectionRect.x, selectionRect.x+selectionRect.width)} y={Math.min(selectionRect.y, selectionRect.y+selectionRect.height)} width={Math.abs(selectionRect.width)} height={Math.abs(selectionRect.height)} fill="#fff33" stroke="#fff" strokeDasharray="4" />}
              </g>
            </svg>
          </div>

          {/* RIGHT PANEL */}
          <div style={{ flex: 1, background: "#111", color: "#fff", padding: 12 }}>
            {selectedShapes.length > 0 ? (
              <>
                <h3>Properties</h3>
                <input type="color" value={selectedShapes[0].color} onChange={(e) => updateShapes(selectedShapes.map(s => ({ ...s, color: e.target.value })))} />
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                  <button onClick={() => updateShapes(selectedShapes.map(s => ({ ...s, x: (s.x||s.x1||0)+10, y: (s.y||s.y1||0)+10, x1: s.x1, y1: s.y1 })))}>Move +10,+10</button>
                  <button onClick={deleteSelected}>Delete</button>
                  <button onClick={copySelected}>Copy</button>
                  <button onClick={pasteClipboard}>Paste</button>
                </div>
              </>
            ) : <p>Select shapes to see properties</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
